import { useEffect, useState, useCallback } from "react";
import { BASE_URL } from "../apiConfig";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import type { AlertColor } from "@mui/material/Alert";

import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";

import {
  Card as MUICard,
  CardContent as MUICardContent,
  CardHeader as MUICardHeader,
  Divider as MUIDivider,
  Box,
  Typography,
  Slider,
} from "@mui/material";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";

type Metrics = {
  totalRevenue: string;
  inventoryTurnover: string;
  stockoutPrevention: string;
  customerSatisfaction: string;
  autoDecisions: string;
  humanInterventions: string;
};

type AgentStatus = {
  agent_id: string;
  agent_name: string;
  agent_type: string;
  total_actions: number;
  completed_actions: number;
  status: string;
  active_pos?: number;
  auto_transfers?: number;
  stockout_prevention?: string;
  price_updates?: number;
  revenue_uplift?: string;
  margin_optimization?: string;
  personalized_offers?: number;
  conversion_rate?: string;
  satisfaction_score?: string;
  delivery_delays?: string | number;
  route_optimizations?: string | number;
  cost_savings?: string | number;
};

type RecentAlert = {
  alert_id: number;
  alert_type: string;
  title: string;
  message: string;
  agent_name: string;
  created_at: string;
  requires_human_intervention: boolean;
  view_details?: string;
  view_summary?: string;
};

type RecentAction = {
  action_id: number;
  agent_name: string;
  action_description: string;
  created_at: string;
  status: string;
  impact_metric: string;
};

type TriggerResponse = {
  success: boolean;
  message: string;
  logs?: string[];
};

function formatLastUpdated(date: Date) {
  const now = new Date();
  const diff = (now.getTime() - date.getTime()) / 1000;
  if (diff < 60) return "Just now";
  if (diff < 3600) return `${Math.floor(diff / 60)} min ago`;
  return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

function formatTimeAgo(dateStr: string) {
  const d = new Date(dateStr);
  const now = new Date();
  const diffSec = (now.getTime() - d.getTime()) / 1000;
  if (Number.isNaN(diffSec)) return dateStr || "";
  if (diffSec < 60) return "Just now";
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)} min ago`;
  if (diffSec < 86400) return `${Math.floor(diffSec / 3600)} hours ago`;
  return d.toLocaleString([], {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

const Card = ({
  title,
  subtitle,
  children,
  headerRight,
}: {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  headerRight?: React.ReactNode;
}) => (
  <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
    <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3 md:px-5 md:py-4">
      <div>
        <h3 className="font-semibold text-slate-900">{title}</h3>
        {subtitle ? (
          <p className="mt-0.5 text-xs text-slate-500">{subtitle}</p>
        ) : null}
      </div>
      {headerRight}
    </div>
    <div className="p-4 md:p-5">{children}</div>
  </div>
);

function StatusChip({
  label,
  tone = "neutral",
}: {
  label: string;
  tone?: "success" | "danger" | "neutral";
}) {
  const map: Record<string, string> = {
    success: "bg-emerald-100 text-emerald-700",
    danger: "bg-rose-100 text-rose-700",
    neutral: "bg-slate-100 text-slate-700",
  };
  return (
    <span
      className={`inline-flex items-center shrink-0 whitespace-nowrap rounded-full
                  font-semibold px-2 py-0.5 text-[10px] sm:px-2.5 sm:py-1 sm:text-xs ${map[tone]}`}
    >
      {label}
    </span>
  );
}

const CheckIcon = () => (
  <svg
    className="w-5 h-5 text-emerald-600"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
  >
    <path
      d="M20 6L9 17l-5-5"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const WarnIcon = () => (
  <svg
    className="w-5 h-5 text-rose-600"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
  >
    <path
      d="M12 9v4m0 4h.01M10.29 3.86l-8.48 14.7A1 1 0 0 0 2.62 20h18.76a1 1 0 0 0 .86-1.44l-8.48-14.7a1.5 1.5 0 0 0-2.58 0z"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const InfoIcon = () => (
  <svg
    className="w-5 h-5 text-blue-600"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
  >
    <circle cx="12" cy="12" r="10" strokeWidth="2" />
    <path
      d="M12 8h.01M11 12h2v4h-2z"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const AlertIcon = ({ type }: { type: string }) => {
  const tone = type.toLowerCase().includes("inventory")
    ? "text-amber-600"
    : type.toLowerCase().includes("pricing")
    ? "text-indigo-600"
    : type.toLowerCase().includes("customer")
    ? "text-fuchsia-600"
    : "text-slate-600";
  return (
    <svg
      className={`w-5 h-5 ${tone}`}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
    >
      <path
        d="M12 9v4m0 4h.01M10.29 3.86l-8.48 14.7A1 1 0 0 0 2.62 20h18.76a1 1 0 0 0 .86-1.44l-8.48-14.7a1.5 1.5 0 0 0-2.58 0z"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};

function AlertListItem({ alert }: { alert: RecentAlert }) {
  const [showDetails, setShowDetails] = useState(false);
  const [showSummary, setShowSummary] = useState(false);

  return (
    <>
      <div className="group relative rounded-xl border border-amber-200 bg-amber-50/60 p-4 transition-all hover:border-amber-300 hover:shadow-md">
        <div className="flex items-start gap-3">
          <AlertIcon type={alert.alert_type} />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 text-sm">
              <span className="font-semibold text-slate-900 truncate">
                {alert.alert_type}
              </span>
              <span className="text-slate-400">•</span>
              <span className="text-slate-500">
                {formatTimeAgo(alert.created_at)}
              </span>
            </div>
            <div className="mt-1 font-medium text-slate-900">{alert.title}</div>
            <div className="mt-1 text-sm text-slate-700 line-clamp-2">
              {alert.message}
            </div>
            {(alert.view_details || alert.view_summary) && (
              <div className="mt-2 flex items-center gap-3">
                {alert.view_details && (
                  <button
                    onClick={() => setShowDetails(true)}
                    className="inline-flex items-center gap-1 text-sm font-medium text-blue-600 hover:text-blue-700"
                  >
                    <svg
                      className="w-4 h-4"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <path d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                    View Details
                  </button>
                )}
                {alert.view_summary && (
                  <button
                    onClick={() => setShowSummary(true)}
                    className="inline-flex items-center gap-1 text-sm font-medium text-green-600 hover:text-green-700"
                  >
                    <svg
                      className="w-4 h-4"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <path d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    View Summary
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Details Modal */}
      <Dialog
        open={showDetails}
        onClose={() => setShowDetails(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <div className="flex items-center gap-3">
            <AlertIcon type={alert.alert_type} />
            <div>
              <div className="font-semibold">{alert.title}</div>
              <div className="text-sm text-slate-500 mt-0.5">
                {formatTimeAgo(alert.created_at)}
              </div>
            </div>
          </div>
        </DialogTitle>
        <DialogContent dividers>
          {alert.view_details ? (
            <div className="bg-slate-50 rounded-lg p-4 font-mono text-sm whitespace-pre-wrap">
              {alert.view_details}
            </div>
          ) : (
            <div className="text-slate-500 italic">
              No additional details available
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDetails(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Summary Modal */}
      <Dialog
        open={showSummary}
        onClose={() => setShowSummary(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <div className="flex items-center gap-3">
            <svg
              className="w-6 h-6 text-green-600"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <div>
              <div className="font-semibold">{alert.title}</div>
              <div className="text-sm text-slate-500 mt-0.5">Summary</div>
            </div>
          </div>
        </DialogTitle>
        <DialogContent dividers>
          {alert.view_summary ? (
            <div className="bg-green-50 rounded-lg p-4 text-sm whitespace-pre-wrap">
              {alert.view_summary}
            </div>
          ) : (
            <div className="text-slate-500 italic">No summary available</div>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowSummary(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

function ActionListItem({ action }: { action: RecentAction }) {
  const status = (action.status || "").toUpperCase();
  const tone =
    status === "COMPLETED"
      ? "success"
      : status === "FAILED"
      ? "danger"
      : "neutral";

  return (
    <div className="group relative rounded-xl border border-slate-200 bg-slate-50/80 p-4 transition-all hover:bg-slate-50 hover:shadow-md">
      <div className="flex items-start gap-3">
        {tone === "success" ? (
          <CheckIcon />
        ) : tone === "danger" ? (
          <WarnIcon />
        ) : (
          <InfoIcon />
        )}
        <div className="flex-1 min-w-0">
          <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-2 min-w-0">
              <span className="font-semibold text-slate-900 truncate">
                {action.agent_name}
              </span>
            </div>
            <div className="sm:flex-shrink-0 self-start sm:self-auto">
              <StatusChip label={status || "—"} tone={tone} />
            </div>
          </div>
          <div className="mt-1 text-sm text-slate-700">
            {action.action_description}
          </div>
          {action.impact_metric && (
            <div className="mt-2 flex items-center gap-2">
              <span className="text-xs font-medium text-blue-700 bg-blue-50 rounded px-2 py-0.5">
                {action.impact_metric}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function AgentStatusCard({ agent }: { agent: AgentStatus }) {
  const [isLoading, setIsLoading] = useState(false);
  const [actionSnackbar, setActionSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: AlertColor;
    logs?: string[];
  }>({ open: false, message: "", severity: "info", logs: [] });

  const getApiEndpoint = (type: string) => {
    switch (type.toLowerCase()) {
      case "inventory":
        return `${BASE_URL}/api/agents/inventory/execute`;
      case "pricing":
        return `${BASE_URL}/api/agents/pricing/execute`;
      case "customer":
        return `${BASE_URL}/api/agents/customer/execute`;
      case "supply_chain":
        return `${BASE_URL}/api/agents/supply_chain/execute`;
      default:
        throw new Error("Invalid agent type");
    }
  };

  const handleTriggerAction = async () => {
    setIsLoading(true);
    try {
      const endpoint = getApiEndpoint(agent.agent_type);
      const response = await fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ agent_id: agent.agent_id }),
      });
      const data: TriggerResponse = await response.json();
      setActionSnackbar({
        open: true,
        message: data.message || (data.success ? "Action triggered" : "Failed"),
        severity: data.success ? "success" : "error",
        logs: data.logs,
      });
    } catch (error: any) {
      setActionSnackbar({
        open: true,
        message: "Error triggering action",
        severity: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const successRate =
    agent.completed_actions === 0
      ? 0
      : Math.round((agent.completed_actions / agent.total_actions) * 100);

  const getAgentIcon = (type: string) => {
    switch (type) {
      case "INVENTORY":
        return (
          <svg
            className="w-8 h-8 text-blue-600"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
          >
            <path d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
          </svg>
        );
      case "PRICING":
        return (
          <svg
            className="w-8 h-8 text-green-600"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
          >
            <path d="M16 8l-8 8m0-8l8 8m-4-12v16" />
          </svg>
        );
      case "SUPPLY_CHAIN":
        return (
          <svg
            className="w-8 h-8 text-purple-600"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
          >
            <path d="M9 17l-5-5 5-5m6 10l5-5-5-5" />
          </svg>
        );
      default:
        return (
          <svg
            className="w-8 h-8 text-orange-600"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
          >
            <circle cx="12" cy="8" r="4" />
            <path d="M18 21v-2a4 4 0 00-4-4H10a4 4 0 00-4 4v2" />
          </svg>
        );
    }
  };

  const getSubtitle = (type: string) => {
    switch (type) {
      case "INVENTORY":
        return "Supply Chain Optimization";
      case "PRICING":
        return "Dynamic Revenue Optimization";
      case "SUPPLY_CHAIN":
        return "Supply Chain Management";
      default:
        return "Experience Personalization";
    }
  };

  const renderExtras = () => {
    const renderMetric = (
      label: string,
      value: string | number | undefined
    ) => (
      <div className="flex justify-between items-center py-2 border-b border-slate-100 last:border-0">
        <span className="text-sm text-slate-600">{label}</span>
        <span className="font-semibold text-sm text-slate-900">
          {value ?? "—"}
        </span>
      </div>
    );

    switch (agent.agent_type) {
      case "INVENTORY":
        return (
          <div className="mt-6 space-y-1">
            {renderMetric("Active Status", agent.status)}
            {renderMetric("Success Rate", `${successRate}%`)}
            {renderMetric("Active POS", agent.active_pos)}
            {renderMetric("Auto Transfers", agent.auto_transfers)}
            {renderMetric("Stockout Prevention", agent.stockout_prevention)}
            {renderMetric("Total Actions", agent.total_actions)}
          </div>
        );
      case "PRICING":
        return (
          <div className="mt-6 space-y-1">
            {renderMetric("Active Status", agent.status)}
            {renderMetric("Success Rate", `${successRate}%`)}
            {renderMetric("Price Updates", agent.price_updates)}
            {renderMetric("Revenue Uplift", agent.revenue_uplift)}
            {renderMetric("Margin Optimization", agent.margin_optimization)}
            {renderMetric("Total Actions", agent.total_actions)}
          </div>
        );
      case "CUSTOMER":
        return (
          <div className="mt-6 space-y-1">
            {renderMetric("Active Status", agent.status)}
            {renderMetric("Success Rate", `${successRate}%`)}
            {renderMetric("Personalized Offers", agent.personalized_offers)}
            {renderMetric("Conversion Rate", agent.conversion_rate)}
            {renderMetric("Satisfaction Score", agent.satisfaction_score)}
            {renderMetric("Total Actions", agent.total_actions)}
          </div>
        );
      case "SUPPLY_CHAIN":
        return (
          <div className="mt-6 space-y-1">
            {renderMetric("Active Status", agent.status)}
            {renderMetric("Success Rate", `${successRate}%`)}
            {renderMetric("Delivery Delays", agent.delivery_delays)}
            {renderMetric("Route Optimizations", agent.route_optimizations)}
            {renderMetric("Cost Savings", agent.cost_savings)}
            {renderMetric("Total Actions", agent.total_actions)}
          </div>
        );
      default:
        return (
          <div className="mt-6 space-y-1">
            {renderMetric("Active Status", agent.status)}
            {renderMetric("Success Rate", `${successRate}%`)}
            {renderMetric("Total Actions", agent.total_actions)}
          </div>
        );
    }
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden hover:border-blue-200 transition-colors">
      <div className="p-6">
        <div className="flex items-center gap-4">
          {getAgentIcon(agent.agent_type)}
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-slate-900">
              {agent.agent_name}
            </h3>
            <p className="text-sm text-slate-600">
              {getSubtitle(agent.agent_type)}
            </p>
          </div>
          <span
            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
              agent.status === "active"
                ? "bg-emerald-100 text-emerald-800"
                : "bg-slate-100 text-slate-800"
            }`}
          >
            {agent.status === "active" ? "Active" : "Paused"}
          </span>
        </div>
        {renderExtras()}
        <div className="mt-6 flex gap-3">
          <button
            onClick={handleTriggerAction}
            disabled={isLoading}
            className={`flex-1 inline-flex justify-center items-center gap-2 rounded-lg px-4 py-2.5 text-sm font-medium ${
              isLoading
                ? "bg-slate-100 text-slate-400 cursor-not-allowed"
                : "bg-blue-600 text-white hover:bg-blue-700 shadow-sm"
            } transition-colors`}
          >
            <svg
              className="w-4 h-4"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
            >
              <path
                d="M13 10V3L4 14h7v7l9-11h-7z"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            {isLoading ? "Processing..." : "Trigger Action"}
          </button>
        </div>
      </div>
      <Snackbar
        open={actionSnackbar.open}
        autoHideDuration={4000}
        onClose={() => setActionSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <MuiAlert
          elevation={6}
          variant="filled"
          onClose={() =>
            setActionSnackbar((prev) => ({ ...prev, open: false }))
          }
          severity={actionSnackbar.severity}
          sx={{ width: "100%" }}
        >
          <div>
            <div>{actionSnackbar.message}</div>
            {actionSnackbar.logs && actionSnackbar.logs.length > 0 && (
              <div className="mt-1 text-xs">
                {actionSnackbar.logs.map((log, i) => (
                  <div key={i}>{log}</div>
                ))}
              </div>
            )}
          </div>
        </MuiAlert>
      </Snackbar>
    </div>
  );
}

function GoalDescription({
  title,
  description,
}: {
  title: string;
  description: string;
}) {
  return (
    <Tooltip
      title={
        <div className="p-1">
          <div className="font-medium mb-1">{title}</div>
          <div className="text-sm opacity-90">{description}</div>
        </div>
      }
      arrow
    >
      <IconButton size="small" sx={{ ml: 1 }}>
        <HelpOutlineIcon sx={{ fontSize: 16 }} />
      </IconButton>
    </Tooltip>
  );
}

function MUISliderRow({
  label,
  description,
  value,
  min = 0,
  max = 100,
  step = 1,
  onChange,
  formatValue,
  loading = false,
}: {
  label: string;
  description: string;
  value: number;
  min?: number;
  max?: number;
  step?: number;
  onChange: (v: number) => void;
  formatValue: (v: number) => string;
  loading?: boolean;
}) {
  return (
    <Box
      sx={{
        display: "grid",
        alignItems: "center",
        gap: { xs: 1, sm: 2 },
        gridTemplateColumns: { xs: "1fr", sm: "220px 1fr 80px" },
      }}
    >
      <Typography
        sx={{
          fontWeight: 600,
          color: "rgb(15 23 42)",
          display: "flex",
          alignItems: "center",
        }}
      >
        {label}
        <GoalDescription title={label} description={description} />
      </Typography>
      <Slider
        value={value}
        min={min}
        max={max}
        step={step}
        onChange={(_, v) => onChange(v as number)}
        disabled={loading}
        aria-label={label}
        sx={{
          color: loading ? "rgb(203 213 225)" : "#0f766e",
          height: 6,
          "& .MuiSlider-rail": { opacity: 1, bgcolor: "rgba(100,116,139,.35)" },
          "& .MuiSlider-track": { border: "none" },
          "& .MuiSlider-thumb": {
            width: 18,
            height: 18,
            boxShadow: "0 0 0 3px rgba(15,118,110,.15)",
          },
        }}
      />
      <Typography
        sx={{
          textAlign: "right",
          fontWeight: 700,
          color: loading ? "rgb(148 163 184)" : "rgb(15 23 42)",
        }}
      >
        {formatValue(value)}
      </Typography>
    </Box>
  );
}

export default function CommandCenterTab() {
  const [metrics, setMetrics] = useState<Metrics | null>(null);
  const [agentStatus, setAgentStatus] = useState<AgentStatus[]>([]);
  const [recentAlerts, setRecentAlerts] = useState<RecentAlert[]>([]);
  const [recentActions, setRecentActions] = useState<RecentAction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const [goalSnackbar, setGoalSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: AlertColor;
  }>({ open: false, message: "", severity: "success" });

  const [isUpdatingGoals, setIsUpdatingGoals] = useState(false);

  const [goals, setGoals] = useState({
    inventory: { stockoutPrevention: 62, inventoryTurnoverTarget: 4.3 },
    pricing: { marginTarget: 35, competitivenessFactor: 7 },
    customer: { satisfactionTarget: 4.7, personalizationLevel: 6 },
  });

  const toDec = (v: number, denom: number) => Number((v / denom).toFixed(2));
  const pendingTimers =
    (window as any).__agentGoalTimers ??
    ((window as any).__agentGoalTimers = new Map<string, number>());

  const postGoalUpdate = useCallback(
    async (
      agentType: "inventory" | "pricing" | "customer",
      key: string,
      value: number
    ) => {
      const endpointMap: Record<typeof agentType, string> = {
        inventory: `${BASE_URL}/api/agent-goals/inventory_crew_agent_001`,
        pricing: `${BASE_URL}/api/agent-goals/pricing_crew_agent_001`,
        customer: `${BASE_URL}/api/agent-goals/customer_crew_agent_001`,
      };

      let payload: Record<string, number> | null = null;
      if (agentType === "inventory") {
        if (key === "stockoutPrevention")
          payload = { stockout_prevention: toDec(value, 100) };
        if (key === "inventoryTurnoverTarget")
          payload = { inventory_turnover_target: toDec(value, 10) };
      } else if (agentType === "pricing") {
        if (key === "marginTarget")
          payload = { margin_target: toDec(value, 100) };
        if (key === "competitivenessFactor")
          payload = { competitiveness_factor: toDec(value, 10) };
      } else if (agentType === "customer") {
        if (key === "satisfactionTarget")
          payload = { satisfaction_target: toDec(value, 5) };
        if (key === "personalizationLevel")
          payload = { personalization_level: toDec(value, 10) };
      }

      if (!payload) return;

      setIsUpdatingGoals(true);
      try {
        const res = await fetch(endpointMap[agentType], {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        const ok = json?.success ?? true;
        setGoalSnackbar({
          open: true,
          message: ok
            ? json?.message || "Goals updated"
            : json?.message || "Failed",
          severity: ok ? "success" : "error",
        });
        if (ok) await fetchDashboard();
      } catch (err) {
        setGoalSnackbar({
          open: true,
          message: "Error updating goal",
          severity: "error",
        });
      } finally {
        setIsUpdatingGoals(false);
      }
    },
    []
  );

  const queueGoalUpdate = useCallback(
    (
      agentType: "inventory" | "pricing" | "customer",
      key: string,
      value: number
    ) => {
      const timerKey = `${agentType}.${key}`;
      if (pendingTimers.has(timerKey))
        clearTimeout(pendingTimers.get(timerKey));
      const t = window.setTimeout(() => {
        postGoalUpdate(agentType, key, value);
        pendingTimers.delete(timerKey);
      }, 500);
      pendingTimers.set(timerKey, t);
    },
    [postGoalUpdate]
  );

  const updateGoal = useCallback(
    (
      agentType: "inventory" | "pricing" | "customer",
      key: string,
      value: number
    ) => {
      setGoals((g) => ({
        ...g,
        [agentType]: { ...(g as any)[agentType], [key]: value },
      }));
      queueGoalUpdate(agentType, key, value);
    },
    [queueGoalUpdate]
  );

  const fromApiDecimal = (key: string, value: number): number => {
    switch (key) {
      case "inventory_turnover_target":
        return Number((value * 10).toFixed(1));
      case "margin_target":
        return Math.round(value * 100);
      case "satisfaction_target":
        return Number((value * 5).toFixed(1));
      default:
        return value;
    }
  };

  const fetchDashboard = useCallback(async () => {
    setLoading(true);
    setIsRefreshing(true);
    setError(null);
    try {
      const res = await fetch(`${BASE_URL}/api/dashboard`);
      if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
      const json = await res.json();
      if (!json.success) throw new Error("API returned unsuccessful response");
      const data = json.data;
      setMetrics(data.metrics);
      setAgentStatus(data.agentStatus);

      const uniqueAlerts = data.recentAlerts.reduce(
        (acc: RecentAlert[], alert: RecentAlert) => {
          const existing = acc.find((a) => a.title === alert.title);
          if (!existing) acc.push(alert);
          else if (new Date(alert.created_at) > new Date(existing.created_at))
            acc[acc.indexOf(existing)] = alert;
          return acc;
        },
        []
      );
      setRecentAlerts(uniqueAlerts);
      setRecentActions(data.recentActions);
      setLastUpdated(new Date());

      const cpv = data.agent_control_panel_values || {};
      setGoals((prev) => ({
        inventory: {
          stockoutPrevention: prev.inventory.stockoutPrevention,
          inventoryTurnoverTarget:
            cpv.inventory_crew_agent_001?.inventory_turnover_target !==
            undefined
              ? fromApiDecimal(
                  "inventory_turnover_target",
                  cpv.inventory_crew_agent_001.inventory_turnover_target
                )
              : prev.inventory.inventoryTurnoverTarget,
        },
        pricing: {
          marginTarget:
            cpv.pricing_crew_agent_001?.margin_target !== undefined
              ? fromApiDecimal(
                  "margin_target",
                  cpv.pricing_crew_agent_001.margin_target
                )
              : prev.pricing.marginTarget,
          competitivenessFactor: prev.pricing.competitivenessFactor,
        },
        customer: {
          satisfactionTarget:
            cpv.customer_crew_agent_001?.satisfaction_target !== undefined
              ? fromApiDecimal(
                  "satisfaction_target",
                  cpv.customer_crew_agent_001.satisfaction_target
                )
              : prev.customer.satisfactionTarget,
          personalizationLevel: prev.customer.personalizationLevel,
        },
      }));
    } catch (err: any) {
      setError(err.message || "Unknown error");
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboard();
    const interval = setInterval(fetchDashboard, 600000);
    return () => clearInterval(interval);
  }, [fetchDashboard]);

  const handleRefresh = () => fetchDashboard();

  if (loading) {
    return (
      <div className="w-full h-[70vh] flex flex-col items-center justify-center text-slate-600">
        <svg
          className="animate-spin h-10 w-10 text-blue-600"
          viewBox="0 0 24 24"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          />
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8v4l5-5-5-5v4a12 12 0 00-12 12h4z"
          />
        </svg>
        <p className="mt-3 font-medium">Loading command center...</p>
      </div>
    );
  }

  if (error) return <div className="p-8 text-red-600">Error: {error}</div>;

  return (
    <div className="relative p-6 md:p-8 space-y-8">
      {/* MAIN CONTENT – dimmed during refresh or goal updates */}
      <div className={(isRefreshing || isUpdatingGoals) ? "opacity-50 pointer-events-none filter blur-sm transition-all duration-200" : "transition-all duration-200"}>
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 bg-white p-4 md:p-6 rounded-xl border border-slate-200 shadow-sm">
          <div>
            <h2 className="text-xl md:text-3xl font-bold text-slate-900 flex items-center gap-2 md:gap-3">
              <svg
                className="w-6 h-6 md:w-8 md:h-8 text-blue-600"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
              >
                <path
                  d="M13 10V3L4 14h7v7l9-11h-7z"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              Command Center
            </h2>
            <p className="mt-1 md:mt-2 text-sm md:text-base text-slate-600">
              Real-time monitoring and control of autonomous retail operations
            </p>
          </div>
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
            <button
              onClick={handleRefresh}
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white px-4 py-2 font-medium text-sm shadow-sm transition-colors"
            >
              <svg
                className="w-4 h-4"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
              >
                <path d="M4 4v5h.582M20 20v-5h-.581" strokeWidth="2" />
                <path
                  d="M5.002 9A7.003 7.003 0 0 1 12 5c2.21 0 4.21.896 5.657 2.343M18.998 15A7.003 7.003 0 0 1 12 19c-2.21 0-4.21-.896-5.657-2.343"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
              Refresh Dashboard
            </button>
            <div className="flex items-center gap-2 text-sm text-slate-600">
              <svg
                className="w-4 h-4"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
              >
                <circle cx="12" cy="12" r="10" strokeWidth="2" />
                <path d="M12 6v6l4 2" strokeWidth="2" strokeLinecap="round" />
              </svg>
              Updated: {lastUpdated ? formatLastUpdated(lastUpdated) : "—"}
            </div>
          </div>
        </div>

        {/* Key Metrics */}
        <Card title="Key Metrics" subtitle="Real-time performance indicators">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* Total Revenue */}
            <div className="rounded-xl border border-slate-200 bg-gradient-to-br from-white to-slate-50 p-4 hover:shadow-md transition-shadow">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 rounded-lg bg-blue-50">
                  <svg
                    className="w-5 h-5 text-blue-600"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                  >
                    <path
                      d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
                <div className="text-xs font-medium text-slate-600">
                  Total Revenue
                </div>
              </div>
              <div className="flex items-baseline gap-2">
                <div className="text-2xl font-bold text-slate-900">
                  {metrics?.totalRevenue ?? "—"}
                </div>
                <div className="text-xs font-medium text-emerald-600">
                  +12.5%
                </div>
              </div>
            </div>

            {/* Inventory Turnover */}
            <div className="rounded-xl border border-slate-200 bg-gradient-to-br from-white to-slate-50 p-4 hover:shadow-md transition-shadow">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 rounded-lg bg-indigo-50">
                  <svg
                    className="w-5 h-5 text-indigo-600"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                  >
                    <path
                      d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
                      strokeWidth="2"
                    />
                  </svg>
                </div>
                <div className="text-xs font-medium text-slate-600">
                  Inventory Turnover
                </div>
              </div>
              <div className="flex items-baseline gap-2">
                <div className="text-2xl font-bold text-slate-900">
                  {metrics?.inventoryTurnover ?? "—"}
                </div>
                <div className="text-xs font-medium text-emerald-600">
                  +2.3%
                </div>
              </div>
            </div>

            {/* Stockout Prevention */}
            <div className="rounded-xl border border-slate-200 bg-gradient-to-br from-white to-slate-50 p-4 hover:shadow-md transition-shadow">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 rounded-lg bg-emerald-50">
                  <svg
                    className="w-5 h-5 text-emerald-600"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                  >
                    <path
                      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                      strokeWidth="2"
                    />
                  </svg>
                </div>
                <div className="text-xs font-medium text-slate-600">
                  Stockout Prevention
                </div>
              </div>
              <div className="flex items-baseline gap-2">
                <div className="text-2xl font-bold text-slate-900">
                  {metrics?.stockoutPrevention ?? "—"}
                </div>
                <div className="text-xs font-medium text-emerald-600">
                  +5.2%
                </div>
              </div>
            </div>

            {/* Customer Satisfaction */}
            <div className="rounded-xl border border-slate-200 bg-gradient-to-br from-white to-slate-50 p-4 hover:shadow-md transition-shadow">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 rounded-lg bg-amber-50">
                  <svg
                    className="w-5 h-5 text-amber-600"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                  >
                    <path
                      d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3zM7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3"
                      strokeWidth="2"
                    />
                  </svg>
                </div>
                <div className="text-xs font-medium text-slate-600">
                  Customer Satisfaction
                </div>
              </div>
              <div className="flex items-baseline gap-2">
                <div className="text-2xl font-bold text-slate-900">
                  {metrics?.customerSatisfaction ?? "—"}
                </div>
                <div className="text-xs font-medium text-emerald-600">+0.2</div>
              </div>
            </div>

            {/* Auto Decisions */}
            <div className="rounded-xl border border-slate-200 bg-gradient-to-br from-white to-slate-50 p-4 hover:shadow-md transition-shadow">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 rounded-lg bg-purple-50">
                  <svg
                    className="w-5 h-5 text-purple-600"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                  >
                    <path
                      d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9"
                      strokeWidth="2"
                    />
                  </svg>
                </div>
                <div className="text-xs font-medium text-slate-600">
                  Auto Decisions
                </div>
              </div>
              <div className="flex items-baseline gap-2">
                <div className="text-2xl font-bold text-slate-900">
                  {metrics?.autoDecisions ?? "—"}
                </div>
                <div className="text-xs font-medium text-emerald-600">+15</div>
              </div>
            </div>

            {/* Human Interventions */}
            <div className="rounded-xl border border-slate-200 bg-gradient-to-br from-white to-slate-50 p-4 hover:shadow-md transition-shadow">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 rounded-lg bg-rose-50">
                  <svg
                    className="w-5 h-5 text-rose-600"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                  >
                    <path
                      d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                      strokeWidth="2"
                    />
                  </svg>
                </div>
                <div className="text-xs font-medium text-slate-600">
                  Human Interventions
                </div>
              </div>
              <div className="flex items-baseline gap-2">
                <div className="text-2xl font-bold text-slate-900">
                  {metrics?.humanInterventions ?? "—"}
                </div>
                <div className="text-xs font-medium text-rose-600">-3</div>
              </div>
            </div>
          </div>
        </Card>

        {/* Agent Status */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 mt-4 md:mt-6">
          {agentStatus.map((agent) => (
            <AgentStatusCard key={agent.agent_id} agent={agent} />
          ))}
        </div>

        {/* Alerts + Actions */}
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 md:gap-6">
          <Card
            title="Critical Alerts & Actions"
            subtitle="Latest items requiring attention"
          >
            <div className="space-y-4">
              {recentAlerts.length === 0 ? (
                <div className="text-center py-8">
                  <svg
                    className="w-12 h-12 mx-auto text-slate-300"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                  >
                    <path
                      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                      strokeWidth="2"
                    />
                  </svg>
                  <p className="mt-2 text-sm text-slate-500">
                    No alerts to display
                  </p>
                </div>
              ) : (
                recentAlerts.map((a) => (
                  <AlertListItem key={a.alert_id} alert={a} />
                ))
              )}
            </div>
          </Card>

          <Card
            title="Autonomous Actions"
            subtitle="Agent executions and outcomes"
          >
            <div className="space-y-4">
              {recentActions.length === 0 ? (
                <div className="text-center py-8">
                  <svg
                    className="w-12 h-12 mx-auto text-slate-300"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                  >
                    <path d="M13 10V3L4 14h7v7l9-11h-7z" strokeWidth="2" />
                  </svg>
                  <p className="mt-2 text-sm text-slate-500">
                    No recent actions
                  </p>
                </div>
              ) : (
                recentActions.map((a) => (
                  <ActionListItem key={a.action_id} action={a} />
                ))
              )}
            </div>
          </Card>
        </div>

        {/* Agent Control Panel */}
        <Card
          title="Agent Control Panel"
          subtitle="Tune agent goals. Changes are saved automatically."
        >
          <div className="space-y-6">
            {/* INVENTORY AGENT */}
            <MUICard variant="outlined" sx={{ borderRadius: 2 }}>
              <MUICardHeader
                titleTypographyProps={{
                  sx: { fontWeight: 700, color: "rgb(15 23 42)" },
                }}
                title="Inventory Agent Goals"
              />
              <MUIDivider />
              <MUICardContent sx={{ position: "relative" }}>
                {isRefreshing && (
                  <div
                    style={{
                      position: "absolute",
                      inset: 0,
                      background: "rgba(255,255,255,0.6)",
                      backdropFilter: "blur(2px)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      zIndex: 10,
                      borderRadius: "8px",
                    }}
                  >
                    <svg
                      className="animate-spin h-6 w-6 text-teal-600"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8v4l5-5-5-5v4a12 12 0 00-12 12h4z"
                      />
                    </svg>
                  </div>
                )}
                <Box sx={{ display: "grid", rowGap: 2.5 }}>
                  <MUISliderRow
                    label="Stockout Prevention"
                    description="Minimize the risk of stockouts to ensure product availability."
                    value={goals.inventory.stockoutPrevention}
                    min={0}
                    max={100}
                    step={1}
                    onChange={(v) =>
                      updateGoal("inventory", "stockoutPrevention", v)
                    }
                    formatValue={(v) => `${Math.round(v)}%`}
                    loading={isRefreshing}
                  />
                  <MUISliderRow
                    label="Inventory Turnover Target"
                    description="Optimize inventory turnover to improve cash flow."
                    value={goals.inventory.inventoryTurnoverTarget}
                    min={0}
                    max={10}
                    step={0.1}
                    onChange={(v) =>
                      updateGoal(
                        "inventory",
                        "inventoryTurnoverTarget",
                        Number(v.toFixed(1))
                      )
                    }
                    formatValue={(v) => `${v.toFixed(1)}x`}
                    loading={isRefreshing}
                  />
                </Box>
              </MUICardContent>
            </MUICard>

            {/* PRICING AGENT */}
            <MUICard variant="outlined" sx={{ borderRadius: 2 }}>
              <MUICardHeader
                titleTypographyProps={{
                  sx: { fontWeight: 700, color: "rgb(15 23 42)" },
                }}
                title="Pricing Agent Goals"
              />
              <MUIDivider />
              <MUICardContent sx={{ position: "relative" }}>
                {isRefreshing && (
                  <div
                    style={{
                      position: "absolute",
                      inset: 0,
                      background: "rgba(255,255,255,0.6)",
                      backdropFilter: "blur(2px)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      zIndex: 10,
                      borderRadius: "8px",
                    }}
                  >
                    <svg
                      className="animate-spin h-6 w-6 text-teal-600"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8v4l5-5-5-5v4a12 12 0 00-12 12h4z"
                      />
                    </svg>
                  </div>
                )}
                <Box sx={{ display: "grid", rowGap: 2.5 }}>
                  <MUISliderRow
                    label="Margin Target"
                    description="Set the target profit margin for products."
                    value={goals.pricing.marginTarget}
                    min={0}
                    max={100}
                    step={1}
                    onChange={(v) => updateGoal("pricing", "marginTarget", v)}
                    formatValue={(v) => `${Math.round(v)}%`}
                    loading={isRefreshing}
                  />
                  <MUISliderRow
                    label="Competitiveness Factor"
                    description="Adjust pricing competitiveness on a scale of 1 to 10."
                    value={goals.pricing.competitivenessFactor}
                    min={0}
                    max={10}
                    step={0.1}
                    onChange={(v) =>
                      updateGoal(
                        "pricing",
                        "competitivenessFactor",
                        Number(v.toFixed(1))
                      )
                    }
                    formatValue={(v) => `${v.toFixed(1)}`}
                    loading={isRefreshing}
                  />
                </Box>
              </MUICardContent>
            </MUICard>

            {/* CUSTOMER AGENT */}
            <MUICard variant="outlined" sx={{ borderRadius: 2 }}>
              <MUICardHeader
                titleTypographyProps={{
                  sx: { fontWeight: 700, color: "rgb(15 23 42)" },
                }}
                title="Customer Agent Goals"
              />
              <MUIDivider />
              <MUICardContent sx={{ position: "relative" }}>
                {isRefreshing && (
                  <div
                    style={{
                      position: "absolute",
                      inset: 0,
                      background: "rgba(255,255,255,0.6)",
                      backdropFilter: "blur(2px)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      zIndex: 10,
                      borderRadius: "8px",
                    }}
                  >
                    <svg
                      className="animate-spin h-6 w-6 text-teal-600"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8v4l5-5-5-5v4a12 12 0 00-12 12h4z"
                      />
                    </svg>
                  </div>
                )}
                <Box sx={{ display: "grid", rowGap: 2.5 }}>
                  <MUISliderRow
                    label="Satisfaction Target"
                    description="Achieve a high level of customer satisfaction."
                    value={goals.customer.satisfactionTarget}
                    min={0}
                    max={5}
                    step={0.1}
                    onChange={(v) =>
                      updateGoal(
                        "customer",
                        "satisfactionTarget",
                        Number(v.toFixed(1))
                      )
                    }
                    formatValue={(v) => `${v.toFixed(1)}/5`}
                    loading={isRefreshing}
                  />
                  <MUISliderRow
                    label="Personalization Level"
                    description="Level of personalization in customer interactions."
                    value={goals.customer.personalizationLevel}
                    min={0}
                    max={10}
                    step={0.5}
                    onChange={(v) =>
                      updateGoal(
                        "customer",
                        "personalizationLevel",
                        Number(v.toFixed(1))
                      )
                    }
                    formatValue={(v) => `${v.toFixed(1)}/10`}
                    loading={isRefreshing}
                  />
                </Box>
              </MUICardContent>
            </MUICard>
          </div>
        </Card>
      </div>

      {/* FULL-PAGE REFRESH OVERLAY */}
      {(isRefreshing || isUpdatingGoals) && (
        <div className="fixed inset-0 flex flex-col items-center justify-center bg-white bg-opacity-60 backdrop-blur-sm z-50">
          <svg
            className="animate-spin h-12 w-12 text-blue-600 mb-4"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8v4l5-5-5-5v4a12 12 0 00-12 12h4z"
            />
          </svg>
          <p className="text-lg font-medium text-slate-700">
            {isUpdatingGoals ? "Updating goals..." : "Refreshing dashboard..."}
          </p>
          <p className="text-sm text-slate-500 mt-1">
            Fetching updated metrics – this may take a few seconds
          </p>
        </div>
      )}

      {/* SNACKBARS */}
      <Snackbar
        open={goalSnackbar.open}
        autoHideDuration={3000}
        onClose={() => setGoalSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <MuiAlert
          elevation={6}
          variant="filled"
          onClose={() => setGoalSnackbar((s) => ({ ...s, open: false }))}
          severity={goalSnackbar.severity}
          sx={{ width: "100%" }}
        >
          {goalSnackbar.message}
        </MuiAlert>
      </Snackbar>
    </div>
  );
}